<template>
  <header class="dashboard-topbar">
    <button
      type="button"
      class="dashboard-topbar__menu-btn lg:hidden"
      aria-label="Ouvrir le menu"
      @click="toggleSidebar"
    >
      <Bars3Icon class="w-6 h-6" />
    </button>

    <div class="dashboard-topbar__title">
      <span class="dashboard-topbar__title-text font-semibold text-slate-800 truncate">{{ routeTitle }}</span>
    </div>

    <div class="dashboard-topbar__actions">
      <!-- Notifications -->
      <div class="relative">
        <button
          type="button"
          class="dashboard-topbar__icon-btn"
          :class="{ 'animate-notification-pulse': unreadCount > 0 }"
          aria-label="Notifications"
          @click="toggleNotificationsDropdown"
        >
          <BellIcon class="w-5 h-5" />
          <span
            v-if="unreadCount > 0"
            class="dashboard-topbar__badge"
          >
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </span>
        </button>
        <Transition name="dropdown">
          <div
            v-if="dropdownOpen"
            class="dashboard-topbar__dropdown"
            role="menu"
          >
            <div class="dashboard-topbar__dropdown-header">
              <span class="font-semibold text-slate-800">Notifications</span>
            </div>
            <ul class="dashboard-topbar__dropdown-list">
              <li
                v-for="(n, index) in notifications"
                :key="notificationKey(n, index)"
                class="dashboard-topbar__dropdown-item"
              >
                <a :href="n.link" class="block" @click="dropdownOpen = false">{{ n.message }}</a>
                <small class="text-slate-500 text-xs">{{ formatDate(n.created_at) }}</small>
              </li>
              <li v-if="notifications.length === 0" class="dashboard-topbar__dropdown-item text-slate-500 text-sm">
                Aucune nouvelle notification.
              </li>
            </ul>
          </div>
        </Transition>
      </div>

      <!-- User avatar + logout -->
      <div class="dashboard-topbar__user">
        <button
          type="button"
          class="dashboard-topbar__logout"
          aria-label="Déconnexion"
          @click="logout"
        >
          <ArrowRightOnRectangleIcon class="w-5 h-5" />
          <span class="hidden sm:inline">Déconnexion</span>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BellIcon, ArrowRightOnRectangleIcon, Bars3Icon } from '@heroicons/vue/24/outline';
import axios from 'axios';
import { logout as performLogout } from '@/utils/auth';
import { isAuthenticatedSession } from '@/utils/sessionAuth';

const emit = defineEmits(['toggle-sidebar']);
const route = useRoute();
const router = useRouter();
const dropdownOpen = ref(false);
const notifications = ref([]);

const routeTitle = computed(() => {
  const name = route.name || '';
  return name || 'Tableau de bord';
});

const unreadCount = computed(() => notifications.value.filter((n) => !n.is_read).length);

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function getValidToken() {
  try {
    return isAuthenticatedSession() ? '1' : null;
  } catch {
    return null;
  }
}

function notificationKey(n, index) {
  return n.id ?? `${n.created_at || index}-${n.message || ''}`;
}

function fetchNotifications() {
  const token = getValidToken();
  if (!token) return;
  axios
    .get('/api/notifications/', { withCredentials: true })
    .then(({ data }) => {
      notifications.value = Array.isArray(data) ? data : [];
    })
    .catch(() => {
      notifications.value = [];
    });
}

function toggleSidebar() {
  emit('toggle-sidebar');
}

/** Ouvrir le panneau = accusé de lecture (aligné sur NotificationsMarkReadView, jamais appelée avant). */
function toggleNotificationsDropdown() {
  dropdownOpen.value = !dropdownOpen.value;
  if (dropdownOpen.value && unreadCount.value > 0) {
    axios
      .post('/api/notifications/mark-read/', {}, { withCredentials: true })
      .then(() => {
        notifications.value = notifications.value.map((n) => ({ ...n, is_read: true }));
      })
      .catch(() => {});
  }
}

function logout() {
  performLogout(router);
}

function onDocumentClick(e) {
  if (!e.target.closest('.dashboard-topbar__actions')) dropdownOpen.value = false;
}

onMounted(() => {
  fetchNotifications();
  document.addEventListener('click', onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
});
</script>

<style scoped>
.dashboard-topbar {
  @apply sticky top-0 z-[900] flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 lg:px-8 h-14 sm:h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/80;
  background: linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}

.dashboard-topbar__menu-btn {
  @apply rounded-lg text-slate-600 hover:bg-slate-100 hover:text-navy transition-all duration-200 flex-shrink-0;
  min-width: 44px;
  min-height: 44px;
  padding: 10px;
  transition: background-color 0.2s ease, box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.dashboard-topbar__menu-btn:hover {
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(15, 23, 42, 0.06);
}
.dashboard-topbar__menu-btn:active {
  transform: scale(0.97);
}

.dashboard-topbar__title {
  @apply flex-1 min-w-0 flex items-center;
}
.dashboard-topbar__title-text {
  @apply text-sm sm:text-base;
}

.dashboard-topbar__actions {
  @apply flex items-center gap-2 sm:gap-4;
}

.dashboard-topbar__icon-btn {
  @apply relative rounded-xl text-slate-600 hover:bg-slate-100 hover:text-navy transition-all duration-200 flex items-center justify-center flex-shrink-0;
  min-width: 44px;
  min-height: 44px;
  padding: 10px;
}
.dashboard-topbar__icon-btn:hover {
  box-shadow: 0 0 0 1px rgba(212, 160, 23, 0.12), 0 4px 12px rgba(212, 160, 23, 0.08);
}
.dashboard-topbar__icon-btn:active {
  transform: scale(0.97);
}

.dashboard-topbar__badge {
  @apply absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold px-1;
}

.dashboard-topbar__dropdown {
  @apply absolute right-0 top-full mt-2 max-h-[360px] overflow-hidden rounded-2xl bg-white shadow-soft-lg border border-slate-200/80;
  width: min(320px, calc(100vw - 24px));
}

.dashboard-topbar__dropdown-header {
  @apply px-4 py-3 border-b border-slate-100;
}

.dashboard-topbar__dropdown-list {
  @apply overflow-y-auto max-h-72 py-2;
}

.dashboard-topbar__dropdown-item {
  @apply px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50/80 transition-colors;
}

.dashboard-topbar__dropdown-item a {
  @apply text-slate-800 font-medium text-sm;
}

.dashboard-topbar__user {
  @apply flex items-center gap-2;
}

.dashboard-topbar__logout {
  @apply flex items-center gap-2 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 text-sm font-medium transition-all duration-200 flex-shrink-0;
  min-height: 44px;
  padding: 10px 12px;
}
.dashboard-topbar__logout:hover {
  box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.15), 0 4px 12px rgba(239, 68, 68, 0.1);
}
.dashboard-topbar__logout:active {
  transform: scale(0.98);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
